<script lang="ts">
import { defineBlock } from '@bagelink/blox'
import { defineSchema, $ } from '@bagelink/vue'
const schema = defineSchema({
	articleType: $.select(
		'Presentation',
		[
			{ label: 'Automatic (from datastore)', value: 'auto' },
			{ label: 'Regular Article', value: 'regular' },
			{ label: 'Series Article', value: 'series' },
			{ label: 'Symposium Article', value: 'symposium' },
			{ label: 'Blog Post', value: 'blog' },
			{ label: 'Op-Ed', value: 'op-ed' },
			{ label: 'Interview', value: 'interview' },
		],
		{
			default: 'auto',
			helpText:
				'Overrides the layout only. The article title, body, authors, media, footnotes, topics, and relationships still come from the datastore.',
		},
	),
})
export const BlockArticle = defineBlock({
	thumbnail: '/block-thumbs/BlockArticle.svg',
	category: 'Content',
	order: 100,
	name: 'BlockArticle',
	label: 'Article',
	icon: 'article',
	schema,
})
</script>
<script setup lang="ts">
import { useSeo } from '@bagelink/blox'
import { useRoute } from 'vue-router'
import { pageSeo } from '@/utils/seo'
import { articleHtmlFields, articleTitleText, sanitizeArticleTitleHtml } from '@/utils/articleHtml'
import { Btn, FormFlow, Image } from '@bagelink/vue'
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import ArticleActions from '@/components/blocks/ArticleActions.vue'
import ArticleBanner from '@/components/blocks/ArticleBanner.vue'
import ArticleCard from '@/components/blocks/ArticleCard.vue'
import AuthorCard from '@/components/blocks/AuthorCard.vue'
import CategoriesList from '@/components/blocks/CategoriesList.vue'
import FootnotesSection from '@/components/blocks/FootnotesSection.vue'
import SeriesNavigation from '@/components/blocks/SeriesNavigation.vue'
import RichHtml from '@/components/RichHtml.vue'
import SymposiumResponses from '@/components/blocks/SymposiumResponses.vue'
import TopicsList from '@/components/blocks/TopicsList.vue'
import { useBoundPageItem, useBoundPageModel } from '@/composables/useBoundPageItem'
import {
	ensureAuthors,
	resolveAuthorForBlock,
	useArticlesWithAuthors,
	useCollection,
} from '@/composables/useDatastore'
import { useNewsletter } from '@/composables/useNewsletter'
import { useSiblingArticles } from '@/composables/useSiblingArticles'


// NOTE: intentionally NO `dataSource` here. `BlockArticle` serves several page
// templates (article / op-ed / interview / blog) whose `$page` binding is stored
// per-page in `data_bindings`. Declaring a `dataSource` makes PageDesigner infer
// a binding from block meta and OVERRIDE the saved one with the wrong collection
// (it picks the first entry), breaking the item resolution. The saved binding
// already supplies `$page` — `useBoundPageItem()` reads it directly.

interface Author {
	name: string
	title: string
	image: string
	bio?: string
	link: string
}

interface Article {
	title: string
	authors: Author[]
	image: string
	link: string
}

const props = withDefaults(
	defineProps<{
		articleType?: 'auto' | 'regular' | 'series' | 'symposium' | 'blog' | 'op-ed' | 'interview'
	}>(),
	{
		articleType: 'auto',
	},
)

/** Map the datastore blogTypeId → the article `type` used by the template. */
const TYPE_BY_ID: Record<string, string> = {
	'5cee70f48e610a3d07c75e7d': 'op-ed',
	'5cee70f48e610adba3c75e7e': 'interview',
	'5cee70f48e610a27a1c75ecf': 'blog',
}

const previewRecord = {
	_id: 'preview-article',
	type: 'regular',
	title: 'A Sample Article',
	subtitle: 'This fixed preview appears only when the editor has no bound record.',
	image: '/og.jpg',
	content: '<p>Article content from the selected datastore record will appear here.</p>',
	publishDate: '2025-01-01',
	authors: [
		{
			name: 'Sample Author',
			title: 'Biblical Scholar',
			image: '/webclip.png',
			link: '/authors',
		},
	],
}

const boundRelationRecord = useBoundPageItem()
const relationRecord = boundRelationRecord.value

// Article records carry `authorIds`, not inline `authors` — resolving those ids
// needs the module-level author index that `ensureAuthors()` builds. The SSG
// snapshot pre-resolves `authors` at build time, so only runtime renders (local
// dev, client navigation) hit the empty case: without this call no author name
// renders at all. `includeBio` is required too — the author card under the
// article prints the bio excerpt, which is otherwise never fetched.
ensureAuthors({ includeBio: true })
const relationField = relationRecord?.symposiumId
	? 'symposiumId'
	: relationRecord?.seriesId
		? 'seriesId'
		: null
const { articles: datastoreArticles } = useArticlesWithAuthors({
	limit: 120,
	...(relationField
		? { q: `${relationField} eq ${JSON.stringify(relationRecord?.[relationField])}`, all: true }
		: {}),
})
const { items: categoryRows } = useCollection<Record<string, any>>('categories', {
	limit: 100,
	pick: ['id', '_id', 'name', 'title', 'slug'],
})
const { items: parashahRows } = useCollection<Record<string, any>>('parashahs', {
	limit: 100,
	pick: ['id', '_id', 'name', 'title', 'slug'],
})
const { items: topicRows } = useCollection<Record<string, any>>('topics', {
	limit: 500,
	pick: ['id', '_id', 'name', 'title', 'slug'],
})
const { items: seriesRows } = useCollection<Record<string, any>>('series', {
	limit: 100,
	pick: ['id', '_id', 'name', 'title', 'slug', 'image'],
})
const { items: symposiumRows } = useCollection<Record<string, any>>('symposia', {
	limit: 100,
	pick: ['id', '_id', 'name', 'title', 'slug', 'image'],
})

function resolveAuthors(rec: Record<string, any>): Author[] {
	if (Array.isArray(rec.authors))
		return rec.authors.map((author: Author & { slug?: string }) => ({
			...author,
			link: author.link || (author.slug ? `/author/${author.slug}` : '/authors'),
		}))
	const list: Author[] = []
	const authorIds = Array.isArray(rec.authorIds)
		? rec.authorIds
		: [rec.authorId, rec.secondAuthorId, rec.thirdAuthorId]
	for (const id of authorIds) {
		const a = resolveAuthorForBlock(id)
		if (a)
			list.push({
				name: a.name,
				title: a.title,
				image: a.image,
				bio: a.bio,
				link: a.slug ? `/author/${a.slug}` : '/authors',
			})
	}
	return list
}

function resolveLinks(
	value: unknown,
	ids: unknown,
	rows: Array<Record<string, any>>,
	prefix: string,
): Array<{ name: string; slug: string }> {
	const fromRow = (row: Record<string, any>) => {
		const name = row.name || row.title || ''
		const slug = row.slug || ''
		return name && slug ? { name, slug: `${prefix}${slug}` } : null
	}
	if (Array.isArray(value)) {
		return value
			.map(item => (typeof item === 'string' ? null : fromRow(item)))
			.filter((item): item is { name: string; slug: string } => !!item)
	}
	if (!Array.isArray(ids)) return []
	const byId = new Map(rows.map(row => [row._id || row.id, row]))
	return ids
		.map(id => byId.get(id))
		.filter((row): row is Record<string, any> => !!row)
		.map(fromRow)
		.filter((item): item is { name: string; slug: string } => !!item)
}

function normalizeArticle(rec: Record<string, any>) {
	const resolvedType = rec.blogTypeId
		? TYPE_BY_ID[rec.blogTypeId] || 'blog'
		: rec.symposiumId
			? 'symposium'
			: rec.seriesId
				? 'series'
				: 'regular'
	return {
		type: resolvedType,
		title: rec.title || rec.name || '',
		...articleHtmlFields(rec),
		image: rec.hideImage ? '' : rec.image || '',
		authors: resolveAuthors(rec),
		categories: [
			...resolveLinks(rec.categories, rec.categoryIds, categoryRows.value, '/categories/'),
			...resolveLinks(rec.parashahs, rec.parshaIds, parashahRows.value, '/parashah/'),
		],
		publishDate: rec.publishDate || rec.created_at || '',
		lastUpdated: rec.lastUpdated || '',
		footnotes:
			Array.isArray(rec.footnotes) || typeof rec.footnotes === 'string' ? rec.footnotes : [],
		topics: resolveLinks(rec.topics, rec.topicIds, topicRows.value, '/topic/'),
		topicIds: Array.isArray(rec.topicIds) ? rec.topicIds : [],
		blogTypeId: rec.blogTypeId as string | undefined,
		slug: (rec.slug as string) || '',
		seriesId: rec.seriesId || '',
		symposiumId: rec.symposiumId || '',
		intervieweeName: rec.intervieweeName || '',
		intervieweeImage: rec.intervieweeImage || '',
		relatedArticleIds: Array.isArray(rec.relatedArticleIds) ? rec.relatedArticleIds : [],
	}
}

const articleModel = useBoundPageModel(normalizeArticle, previewRecord)
const article = computed(() => articleModel.value ?? normalizeArticle({}))
const boundSeoRecord = useBoundPageItem()
const route = useRoute()
useSeo(() =>
	boundSeoRecord.value
		? pageSeo(route.path, boundSeoRecord.value, resolveAuthors(boundSeoRecord.value))
		: null,
)

// Sibling navigation for blog / op-ed / interview pages (same blogTypeId).
const currentSlug = computed(() => article.value.slug)
const currentBlogTypeId = computed(() => article.value.blogTypeId)
const currentType = computed(() => article.value.type)
const { olderItem, newerItem, latest } = useSiblingArticles(
	currentSlug,
	currentBlogTypeId,
	currentType,
)

// Computed properties
const articleType = computed(() =>
	props.articleType !== 'auto' ? props.articleType : article.value.type || 'regular',
)

const title = computed(() => article.value.title)
const subtitle = computed(() => article.value.subtitle)
const image = computed(() => article.value.image)
const imageCaption = computed(() => article.value.imageCaption)
const authors = computed(() => article.value.authors)
const categories = computed(() => article.value.categories)
const content = computed(() => article.value.content)
const footnotes = computed(() => article.value.footnotes)
const topics = computed(() => article.value.topics)
const publishDate = computed(() => article.value.publishDate)
const lastUpdated = computed(() => article.value.lastUpdated)

/**
 * Byline label: the honorific stored on the author record ("Prof.", "Dr.",
 * "Prof. Rabbi") always precedes the name, so a reader sees the same
 * "Prof. Rabbi Herbert Basser" form everywhere an author is credited. Records
 * without a title render the bare name — never a placeholder.
 */
function authorLabel(author: Author) {
	return [author.title?.trim(), author.name?.trim()].filter(Boolean).join(' ')
}

/** "Latest Posts" rail — real same-type siblings only. */
const recentPosts = computed<Article[]>(() =>
	latest.value.map(s => ({
		title: s.title,
		image: s.image,
		link: s.link,
		authors: s.authors.map(a => ({
			...a,
			link: a.slug ? `/author/${a.slug}` : '/authors',
		})),
	})),
)

function toArticleCard(record: Record<string, any>): Article {
	return {
		title: record.title || '',
		image: record.image || '',
		link: record.slug ? `/article/${record.slug}` : '',
		authors: resolveAuthors(record),
	}
}

const relatedArticles = computed(() => {
	const ids = new Set(article.value.relatedArticleIds)
	if (!ids.size) return []
	return datastoreArticles.value
		.filter((record: any) => ids.has(record._id || record.id))
		.map(toArticleCard)
})

const intervieweeName = computed(() => article.value.intervieweeName)
const intervieweeBio = computed(() => article.value.intervieweeBio)
const intervieweeImage = computed(() => article.value.intervieweeImage)
const symposiumContributors = computed(() => {
	if (!article.value.symposiumId) return []
	return datastoreArticles.value
		.filter(
			(record: any) =>
				record.symposiumId === article.value.symposiumId && record.slug !== article.value.slug,
		)
		.slice()
		.sort(
			(a: any, b: any) =>
				new Date(b.publishDate || 0).getTime() - new Date(a.publishDate || 0).getTime(),
		)
		.slice(0, 2)
		.map((record: any) => {
			const author = resolveAuthors(record)[0]
			return {
				name: author?.name || '',
				title: author?.title || '',
				response: record.title || '',
				link: `/article/${record.slug}`,
				authorLink: author?.link || '',
				authorImage: author?.image || '/og.jpg',
			}
		})
})

/** Prev/Next between same-type items (older ⇄ newer by publish date). */
const blogNavigation = computed(() => ({
	previousArticleTitle: olderItem.value?.title || '',
	previousArticleLink: olderItem.value?.link || '',
	nextArticleTitle: newerItem.value?.title || '',
	nextArticleLink: newerItem.value?.link || '',
}))

const seriesNavigation = computed(() => {
	if (!article.value.seriesId) return undefined
	const siblings = datastoreArticles.value
		.filter((record: any) => record.seriesId === article.value.seriesId && record.slug)
		.slice()
		.sort((a: any, b: any) => (a.seriesOrder ?? 999) - (b.seriesOrder ?? 999))
	const index = siblings.findIndex((record: any) => record.slug === article.value.slug)
	if (index < 0) return undefined
	const previous = siblings[index - 1]
	const next = siblings[index + 1]
	return {
		previousArticleTitle: previous?.title || '',
		previousArticleLink: previous?.slug ? `/article/${previous.slug}` : '',
		nextArticleTitle: next?.title || '',
		nextArticleLink: next?.slug ? `/article/${next.slug}` : '',
		seriesIndexPage: '',
	}
})

const formattedDate = computed(() => {
	if (!publishDate.value) return ''
	const date = new Date(publishDate.value)
	const months = [
		'January',
		'February',
		'March',
		'April',
		'May',
		'June',
		'July',
		'August',
		'September',
		'October',
		'November',
		'December',
	]
	return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`
})

const isBlogType = computed(() => ['blog', 'op-ed', 'interview'].includes(articleType.value))
const hasActions = computed(() => !['blog', 'op-ed', 'interview'].includes(articleType.value))
const showSidebar = computed(() => isBlogType.value)
const showSeriesHeader = computed(() => articleType.value === 'series')
const showSymposiumHeader = computed(() => articleType.value === 'symposium')
const showInterviewBio = computed(
	() => articleType.value === 'interview' && !!(intervieweeBio.value || intervieweeImage.value),
)
const showSeriesNavigation = computed(
	() =>
		articleType.value === 'series' &&
		!!(seriesNavigation.value?.previousArticleTitle || seriesNavigation.value?.nextArticleTitle),
)
const showBlogNavigation = computed(() => false)
const showSymposiumContributors = computed(
	() => articleType.value === 'symposium' && (symposiumContributors.value?.length ?? 0) > 0,
)
const topicIds = computed(() => new Set(article.value.topicIds))
const relatedTopicArticles = computed(() => {
	const explicit = relatedArticles.value
	if (explicit.length) return explicit
	if (!topicIds.value.size) return []
	return datastoreArticles.value
		.filter(
			(record: any) =>
				record.slug &&
				record.slug !== article.value.slug &&
				Array.isArray(record.topicIds) &&
				record.topicIds.some((id: string) => topicIds.value.has(id)),
		)
		.slice(0, 4)
		.map(toArticleCard)
})
const showRelatedArticles = computed(
	() =>
		(relatedTopicArticles.value?.length ?? 0) > 0 &&
		!['series', 'symposium'].includes(articleType.value),
)
const showRegularImage = computed(
	() => !!image.value && !showSeriesHeader.value && !showSymposiumHeader.value,
)
const bannerType = computed(() =>
	articleType.value === 'series' || articleType.value === 'symposium'
		? (articleType.value as 'series' | 'symposium')
		: 'series',
)
const linkedCollection = computed(() =>
	articleType.value === 'symposium' ? symposiumRows.value : seriesRows.value,
)
const linkedGroupId = computed(() =>
	articleType.value === 'symposium' ? article.value.symposiumId : article.value.seriesId,
)
const linkedGroup = computed(
	() =>
		linkedCollection.value.find(
			(item: any) => (item._id || item.id || item.slug) === linkedGroupId.value,
		) as Record<string, any> | undefined,
)
const bannerTitle = computed(
	() => linkedGroup.value?.title || linkedGroup.value?.name || title.value,
)
const bannerImage = computed(() => linkedGroup.value?.image || image.value || '/og.jpg')
const articleElement = ref<HTMLElement | null>(null)
const showNewsletterPanel = ref(false)
const newsletterPanelDismissed = ref(false)
const {
	formData,
	state,
	schema: newsletterSchema,
	loadError,
	loadNewsletterForm,
	submitNewsletter,
} = useNewsletter()

function updateNewsletterPanelVisibility() {
	if (!articleElement.value || newsletterPanelDismissed.value) return
	const articleRect = articleElement.value.getBoundingClientRect()
	const articleTop = window.scrollY + articleRect.top
	const halfwayPoint = articleTop + articleElement.value.offsetHeight / 2
	showNewsletterPanel.value = window.scrollY + window.innerHeight >= halfwayPoint
}

function dismissNewsletterPanel() {
	newsletterPanelDismissed.value = true
	showNewsletterPanel.value = false
}

onMounted(() => {
	window.addEventListener('scroll', updateNewsletterPanelVisibility, { passive: true })
	window.addEventListener('resize', updateNewsletterPanelVisibility)
	updateNewsletterPanelVisibility()
	void loadNewsletterForm()
})

onBeforeUnmount(() => {
	window.removeEventListener('scroll', updateNewsletterPanelVisibility)
	window.removeEventListener('resize', updateNewsletterPanelVisibility)
})
</script>

<template>
	<div ref="articleElement" class="article-page w1100px pb-4 m_w-100p m_pb-1">
		<Transition name="fade">
			<aside
				v-if="showNewsletterPanel"
				class="fixed end-0 center-y z-99 w-300px bg-gray-50 p-2 m_hide hidePrint"
			>
				<Btn
					thin
					round
					icon="close"
					color="black"
					class="absolute -top-05 -start-05"
					@click="dismissNewsletterPanel"
				/>
				<h2 class="txt18 italic m-0 line-height-13 pb-1 txt-center balance">
					Stay updated with the latest scholarship
				</h2>
				<p v-if="loadError" class="txt13 color-red txt-center">{{ loadError }}</p>
				<FormFlow
					v-else-if="newsletterSchema"
					v-model="formData"
					:schema="newsletterSchema"
					class="light-input-inner"
					:on-submit="submitNewsletter"
				>
					<template #submit="{ formState }">
						<Btn
							type="submit"
							color="primary"
							value="Subscribe"
							full-width
							:loading="formState === 'submitting'"
							class="rounded"
						/>
					</template>
				</FormFlow>
				<p v-if="state === 'success'" class="txt13 txt-center pt-1">Thank you for subscribing!</p>
				<p v-else-if="state === 'error'" class="txt13 color-red txt-center pt-1">
					Something went wrong. Please try again.
				</p>
			</aside>
		</Transition>

		<!-- Article Banner for Series and Symposium -->
		<ArticleBanner
			v-if="showSeriesHeader || showSymposiumHeader"
			:type="bannerType"
			:title="bannerTitle || ''"
			:image="bannerImage"
			:link="linkedGroup?.slug ? `/${bannerType}/${linkedGroup.slug}` : `/${bannerType}/`"
		/>

		<div class="m_px-1" :class="{ 'with-sidebar grid gap-2': isBlogType }">
			<!-- Main Article Content -->
			<article>
				<!-- Blog/Op-ed/Interview Header -->
				<div v-if="isBlogType" class="txt17 -mb-2 pt-3 m_mb-0 m_-mt-2">
					<router-link :to="`/${articleType}`" class="decoration-none color-black">
						{{ articleType === 'blog' ? 'Blog' : articleType === 'op-ed' ? 'Op-Ed' : 'Interview' }}
					</router-link>
				</div>

				<!-- Article Header -->
				<header class="pt-2 m_pt-05 pb-2 m_pb-1">
					<h1
						class="txt40 m_txt32 regular pb-05 w830px mx-auto"
						:class="{ 'txt-center line-height-17': !isBlogType, 'my-0 line-height-14': isBlogType }"
						v-html="sanitizeArticleTitleHtml(title)"
					/>
					<h2
						v-if="subtitle"
						class="light txt20 m_txt17 line-height-18"
						:class="{ 'w720px mx-auto light': !isBlogType }"
						v-html="subtitle"
					/>
				</header>

				<div
					class="grid gap-1 align-items-start m_block"
					:class="{ 'article-content-grid': !isBlogType }"
				>
					<!-- Article Actions (Print, Share, Cite) -->
					<ArticleActions
						v-if="hasActions"
						:article="{ title, authors, image }"
						layout="vertical"
						class="pt-4 m_pt-0 hidePrint"
					/>

					<!-- Article Content -->
					<div>
						<!-- Authors -->
						<div
							class="pb-2 m_pt-1-5"
							:class="{ 'flex space-between w-100p align-items-center': isBlogType }"
						>
							<div class="article-byline">
								<router-link
									v-for="(author, index) in authors"
									:key="index"
									:to="author.link"
									class="txt20 decoration-none color-black"
								>
									{{ authorLabel(author) }}{{ index < (authors?.length ?? 0) - 1 ? ', ' : '' }}
								</router-link>
							</div>
							<div v-if="!hasActions" class="flex gap-1 hidePrint">
								<ArticleActions
									:article="{ title, authors, image }"
									class=""
									:show-cite="false"
									:show-share="false"
								/>
								<div v-if="formattedDate" class="txt14 color-gray-70 w-70px txt-center">
									{{ formattedDate }}
								</div>
							</div>
						</div>
						<!-- Regular Article Image -->
						<div v-if="showRegularImage" class="w720px mx-auto mb-2">
							<figure class="m-0">
								<Image
									:src="image"
									:alt="articleTitleText(title)"
									class="w-100p max-h-500px mx-auto article-image"
								/>
								<template v-if="imageCaption">
									<figcaption
										class="txt13 mb-2 light txt-center line-height-17"
										v-html="imageCaption"
									/>
								</template>
							</figure>
						</div>
						<!-- Interview Bio Box -->
						<div
							v-if="showInterviewBio"
							class="bg-gray p-2 mb-2 flex gap-2 align-items-start m_column m_align-items-center m_p-1"
						>
							<Image
								v-if="intervieweeImage"
								:src="intervieweeImage"
								:alt="intervieweeName"
								ratio="3-4"
								class="ratio-3-4 h-160px cover"
							/>
							<div>
								<h3 v-if="intervieweeName" class="m-0 pb-05 txt20 line-height-13">
									{{ intervieweeName }}
								</h3>
								<div class="txt17 light" v-html="intervieweeBio" />
							</div>
						</div>

						<!-- Process content with footnote tooltips -->
						<div class="article-content">
							<RichHtml class="sym-rtf first-para txt17 m_txt15 line-height-18" :html="content" />
						</div>
					</div>
					<template v-if="!isBlogType">
						<!-- Categories -->
						<CategoriesList
							v-if="categories?.length"
							:items="categories.map(c => ({ label: c.name, slug: c.slug }))"
							title="Categories:"
							class="mt-4 m_none hidePrint"
						/>
					</template>
				</div>

				<!-- Footnotes -->
				<FootnotesSection
					:footnotes="footnotes"
					:publish-date="publishDate"
					:last-updated="lastUpdated"
				/>
				<!-- Series Navigation -->
				<SeriesNavigation
					v-if="showSeriesNavigation"
					v-bind="seriesNavigation!"
					class="hidePrint"
				/>

				<!-- Blog / Op-Ed / Interview prev-next navigation (same type) -->
				<div v-if="showBlogNavigation" class="w720px mx-auto pt-3 border-top hidePrint m_px-1">
					<nav class="grid grid-wrap-2 gap-2">
						<RouterLink
							v-if="blogNavigation.previousArticleTitle"
							:to="blogNavigation.previousArticleLink"
							class="decoration-none hover"
						>
							<p class="txt15 color-gray-70">Previous</p>
							<h4 class="txt21 bold line-height-14 m-0 pt-1">
								{{ blogNavigation.previousArticleTitle }}
							</h4>
						</RouterLink>
						<RouterLink
							v-if="blogNavigation.nextArticleTitle"
							:to="blogNavigation.nextArticleLink"
							class="decoration-none hover txt-end"
						>
							<p class="txt15 color-gray-70">Next</p>
							<h4 class="txt21 bold line-height-14 m-0 pt-1">
								{{ blogNavigation.nextArticleTitle }}
							</h4>
						</RouterLink>
					</nav>
				</div>

				<!-- Author Cards -->
				<div class="w720px mx-auto pt-2">
					<AuthorCard
						v-for="(author, index) in authors"
						:key="index"
						name=""
						:show-name="false"
						article-layout
						:author="author"
						:rows="3"
					/>
				</div>

				<!-- Topics -->
				<TopicsList
					v-if="topics?.length"
					:items="topics.map(t => ({ label: t.name, slug: t.slug }))"
					title="Related Topics:"
					class="w720px mx-auto hidePrint"
				/>

				<!-- Symposium Contributors -->
				<SymposiumResponses
					v-if="showSymposiumContributors"
					class="hidePrint"
					:symposium-title="linkedGroup?.title || linkedGroup?.name || ''"
					:symposium-link="linkedGroup?.slug ? `/symposium/${linkedGroup.slug}` : '/symposium/'"
					:contributors="
						(symposiumContributors || []).map((contrib, index) => ({
							id: index + 1,
							title: contrib.response,
							author: contrib.name,
							authorImage: contrib.authorImage,
							authorLink: contrib.authorLink,
							name: contrib.title,
							link: contrib.link,
						}))
					"
				/>

				<!-- Related Articles -->
				<div v-if="showRelatedArticles" class="w720px mx-auto hidePrint">
					<h3 v-if="!isBlogType" class="txt15 regular my-0 pt-1">Essays on Related Topics:</h3>
					<div class="grid grid-wrap-2 gap-1">
						<ArticleCard
							v-for="article in relatedTopicArticles.slice(0, 2)"
							:key="article.link"
							:article="article"
						/>
					</div>
				</div>
			</article>

			<!-- Sidebar for Blog/Op-ed/Interview -->
			<aside v-if="showSidebar" class="bg-gray mt-3 p-1-5 m_none hidePrint">
				<h3 class="m-0 pb-1 txt17 light color-gray-70">Latest Posts</h3>
				<router-link
					v-for="post in recentPosts"
					:key="post.link"
					:to="post.link"
					class="decoration-none hover flex gap-1 align-items-start mb-2 hover"
				>
					<Image
						:src="post.image"
						:alt="articleTitleText(post.title)"
						ratio="5-4"
						class="ratio-5-4 h-60px cover"
					/>
					<p class="txt13 light line-height-16" v-html="sanitizeArticleTitleHtml(post.title)" />
				</router-link>
			</aside>
		</div>
	</div>
</template>

<style>
.article-image .bgl-image-frame-img {
	width: auto !important;
	margin: 0 auto !important;
	border-radius: 0 !important;
	object-fit: contain !important;
}

.article-byline {
	display: flex;
	flex-wrap: wrap;
	column-gap: 0.35rem;
	row-gap: 0.15rem;
}

.light-input-inner input {
	background: var(--bgl-popup-bg) !important;
	box-shadow:
		0 1px 5px 0 rgba(0, 0, 0, 0.1),
		0 1px 2px -1px rgba(0, 0, 0, 0.1) !important;
	outline: 1px solid var(--bgl-border-color) !important;
	transition:
		box-shadow 0.2s ease,
		outline-color 0.2s ease;
}
</style>
